import type {
  AssessmentsAccumulationMappingsResponse,
  AssessmentsAccumulationResponse,
  AssessmentsComparisonDetailResponse,
  AssessmentsProjectResponse,
} from '@/entities/assessments/api/generated/model';

type FlattenedAccumulation = {
  accumulationId: string;
  accumulation: AssessmentsAccumulationResponse;
  project: AssessmentsProjectResponse;
  category1Code: string;
  category1Name: string;
  category2Code: string;
  category2Name: string;
  category3Code: string;
  category3Name: string;
};

/**
 * プロジェクト階層を走査して全 accumulation をテーブル表示順でフラット化する。
 * useMemo と組み合わせて使用し、comparisonData が変わらない限り再計算を避ける。
 */
export const flattenAccumulations = (
  projects: AssessmentsProjectResponse[],
): FlattenedAccumulation[] => {
  const result: FlattenedAccumulation[] = [];

  for (const project of projects) {
    for (const setsu of project.setsus) {
      if (!setsu.saisetsus?.length && setsu.accumulation) {
        result.push({
          accumulationId: setsu.accumulation.id,
          accumulation: setsu.accumulation,
          project,
          category1Code: setsu.setsu_code,
          category1Name: setsu.setsu_name,
          category2Code: '',
          category2Name: '',
          category3Code: '',
          category3Name: '',
        });
        continue;
      }

      for (const saisetsu of setsu.saisetsus ?? []) {
        if (!saisetsu.saisaisetsus?.length && saisetsu.accumulation) {
          result.push({
            accumulationId: saisetsu.accumulation.id,
            accumulation: saisetsu.accumulation,
            project,
            category1Code: setsu.setsu_code,
            category1Name: setsu.setsu_name,
            category2Code: saisetsu.saisetsu_code,
            category2Name: saisetsu.saisetsu_name,
            category3Code: '',
            category3Name: '',
          });
          continue;
        }

        for (const saisaisetsu of saisetsu.saisaisetsus ?? []) {
          result.push({
            accumulationId: saisaisetsu.accumulation.id,
            accumulation: saisaisetsu.accumulation,
            project,
            category1Code: setsu.setsu_code,
            category1Name: setsu.setsu_name,
            category2Code: saisetsu.saisetsu_code,
            category2Name: saisetsu.saisetsu_name,
            category3Code: saisaisetsu.saisaisetsu_code,
            category3Name: saisaisetsu.saisaisetsu_name,
          });
        }
      }
    }
  }

  return result;
};

/**
 * フラット化済みの accumulation リストから指定IDのデータを検索し、
 * サイドパネル表示用の placeholder データを構築する
 */
export const buildPlaceholderData = ({
  allAccumulations,
  accumulationId,
  comparisonData,
}: {
  allAccumulations: FlattenedAccumulation[];
  accumulationId: string;
  comparisonData: AssessmentsComparisonDetailResponse;
}): AssessmentsAccumulationMappingsResponse | null => {
  const currentIndex = allAccumulations.findIndex((item) => item.accumulationId === accumulationId);

  if (currentIndex === -1) {
    return null;
  }

  const current = allAccumulations[currentIndex];
  if (!current) {
    return null;
  }

  return {
    assessments_accumulation_id: current.accumulationId,
    base_fiscal_year: comparisonData.base_fiscal_year,
    compared_fiscal_year: comparisonData.compared_fiscal_year,
    // サイドパネルのコンポーネントでは未使用だが型を満たすために設定
    accounting_type: {
      id: '',
      code: current.project.accounting_type_code,
      name: current.project.accounting_type_name,
      year: comparisonData.base_fiscal_year,
    },
    kan: current.project.kan,
    ko: current.project.ko,
    moku: current.project.moku,
    project_code: current.project.project_code,
    project_name: current.project.project_name,
    project_category1_code: current.category1Code,
    project_category1_name: current.category1Name,
    project_category2_code: current.category2Code,
    project_category2_name: current.category2Name,
    project_category3_code: current.category3Code,
    project_category3_name: current.category3Name,
    mappings: current.accumulation.mappings,
    bases: current.accumulation.bases,
    compareds: current.accumulation.compareds,
    comment: current.accumulation.comment ?? '',
    // memo はキャッシュに含まれないため、詳細API取得後に上書きされる
    memo: '',
    accumulation_tags: current.accumulation.accumulation_tags,
  };
};
